/**
 * « Prochaines sessions près de chez vous » (D-139): the official sessions of the learner's country
 * for this exam (and Goethe level), only those verified by the team, with the registration deadline,
 * the official link, a deadline reminder (consent), « use this date for my plan » and the guide.
 * Without a verified date: « Dates à venir » and the « tell me » alert of a city.
 */

import {
  ExamEnum,
  getAcademyMeExamsListQueryKey,
  getAcademyMeRetrieveQueryKey,
  getExamDatesMeRetrieveQueryKey,
  useAcademyMeExamsCreate,
  useAcademyMePartialUpdate,
  useExamDatesMeAlertsCreate,
  useExamDatesMeRemindersCreate,
  useExamDatesMeRemindersDestroy,
  useExamDatesMeRetrieve,
} from "@agencyos/api-client";
import { deadlineState, parseGuide, sessionFee, upcomingSessions } from "@agencyos/core";
import { Badge, Button, GlassCard, SelectField } from "@agencyos/ui-web";
import { useQueryClient } from "@tanstack/react-query";
import { BellRing, CalendarRange, ExternalLink } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import "./examDatesCard.css";
import { dateText } from "./shared";
import { CardTitle } from "./ui";

const TONE = { none: "default", open: "info", soon: "warning", last: "danger", closed: "default" } as const;

function todayIso(): string {
  return new Date().toLocaleDateString("en-CA", { timeZone: "Africa/Douala" });
}

export function ExamDatesCard({ exam, level = "" }: { exam: string; level?: string }) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const params = { exam, ...(level ? { level } : {}) };
  const near = useExamDatesMeRetrieve(params, { query: { staleTime: 300_000, retry: false } });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: getExamDatesMeRetrieveQueryKey(params) });
  const remind = useExamDatesMeRemindersCreate({ mutation: { onSuccess: refresh } });
  const forget = useExamDatesMeRemindersDestroy({ mutation: { onSuccess: refresh } });
  const alert = useExamDatesMeAlertsCreate({ mutation: { onSuccess: refresh } });
  const country = useAcademyMePartialUpdate({
    mutation: {
      onSuccess: () => {
        refresh();
        void queryClient.invalidateQueries({ queryKey: getAcademyMeRetrieveQueryKey() });
      },
    },
  });
  const plan = useAcademyMeExamsCreate({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: getAcademyMeRetrieveQueryKey() });
        void queryClient.invalidateQueries({ queryKey: getAcademyMeExamsListQueryKey() });
      },
    },
  });
  const [chosen, setChosen] = useState("");
  const data = near.data?.status === 200 ? near.data.data : undefined;
  if (!data) return null;
  const today = todayIso();
  const sessions = upcomingSessions(data.sessions, today, 3);
  const guide = parseGuide(data.guide);
  const examCode = Object.values(ExamEnum).find((code) => code === exam) ?? ExamEnum.tcf_canada;

  return (
    <GlassCard as="section" className="ac-card ed-near" aria-labelledby="ed-near-title">
      <CardTitle id="ed-near-title" icon={<CalendarRange size={16} />} tone="info">
        {t("examDates.near.title")}
      </CardTitle>
      {!data.country ? (
        <form
          className="ac-form"
          onSubmit={(event) => {
            event.preventDefault();
            if (chosen) country.mutate({ data: { country: chosen } });
          }}
        >
          <p className="ac-secondary">{t("examDates.near.chooseCountry")}</p>
          <SelectField
            label={t("examDates.fields.country")}
            value={chosen}
            onChange={(e) => setChosen(e.target.value)}
            options={[{ value: "", label: t("academy.goals.choose") }, ...data.countries.map((c) => ({ value: String(c.code), label: String(c.name) }))]}
          />
          <Button type="submit" disabled={!chosen || country.isPending}>
            {t("examDates.near.save")}
          </Button>
        </form>
      ) : (
        <>
          <p className="ac-secondary">
            {t("examDates.near.where", { exam: data.exam_name + (data.level ? ` ${data.level}` : ""), country: data.country_name })}
          </p>
          {sessions.length ? (
            <ul className="ed-near__list">
              {sessions.map((s) => {
                const state = deadlineState(s, today);
                const fee = sessionFee(s);
                return (
                  <li key={s.id} className="ed-near__item">
                    <div className="ed-near__head">
                      <strong>{s.dates.map((d) => dateText(d, i18n.language)).join(" · ")}</strong>
                      <span>
                        {s.city} · {s.center_name}
                      </span>
                    </div>
                    <div className="ed-near__meta">
                      {s.registration_deadline ? (
                        <Badge tone={TONE[state.tone]}>
                          {state.tone === "closed"
                            ? t("examDates.near.closed")
                            : t("examDates.near.deadline", { date: dateText(s.registration_deadline, i18n.language), count: state.daysLeft ?? 0 })}
                        </Badge>
                      ) : (
                        <span className="ac-secondary">{t("examDates.near.noDeadline")}</span>
                      )}
                      {fee ? <span className="ac-secondary">{t("examDates.near.fee", { fee, date: dateText(s.fee_noted_on, i18n.language) })}</span> : null}
                    </div>
                    <div className="ac-card__actions">
                      <a className="ed-near__link" href={s.registration_url} target="_blank" rel="noopener noreferrer">
                        {t("examDates.near.register")} <ExternalLink size={12} aria-hidden="true" />
                      </a>
                      {s.registration_deadline && state.tone !== "closed" ? (
                        s.reminder ? (
                          <Button size="sm" variant="secondary" loading={forget.isPending} onClick={() => forget.mutate({ sessionId: s.id })}>
                            {t("examDates.near.reminderOff")}
                          </Button>
                        ) : (
                          <Button size="sm" variant="tinted" icon={<BellRing size={14} />} loading={remind.isPending} onClick={() => remind.mutate({ data: { session: s.id, consent: true } })}>
                            {t("examDates.near.reminderOn")}
                          </Button>
                        )
                      ) : null}
                      <Button size="sm" variant="ghost" loading={plan.isPending} onClick={() => plan.mutate({ data: { exam: examCode, exam_date: s.first_date } })}>
                        {t("examDates.near.usePlan")}
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="ed-near__empty">
              <p>
                <strong>{t("examDates.near.none")}</strong>
              </p>
              {data.cities.length ? (
                <p className="ac-secondary">{t("examDates.near.cities", { cities: data.cities.map((c) => c.city).join(", ") })}</p>
              ) : (
                <p className="ac-secondary">{t("examDates.near.noCentre")}</p>
              )}
              {data.alerts.includes("") ? (
                <Badge tone="success">{t("examDates.near.alertOn")}</Badge>
              ) : (
                <Button size="sm" variant="tinted" icon={<BellRing size={14} />} loading={alert.isPending} onClick={() => alert.mutate({ data: { exam: examCode, level, city: "", consent: true } })}>
                  {t("examDates.near.alert")}
                </Button>
              )}
            </div>
          )}
          <p className="ac-secondary ed-near__consent">{t("examDates.near.consent")}</p>
          {guide.validity ? <p className="ac-secondary">{guide.validity.text}</p> : null}
          <a className="ed-near__link" href={data.guide_url} target="_blank" rel="noopener noreferrer">
            {t("examDates.near.guide")} <ExternalLink size={12} aria-hidden="true" />
          </a>
        </>
      )}
      <p className="ac-secondary ed-near__disclaimer">{data.disclaimer}</p>
    </GlassCard>
  );
}
